import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";

// GraphQL queries
const STOP_EMAIL_LIST_QUERY = gql`
  query StopEmailList($filter: StopEmailFilter, $params: PaginationAndSorting) {
    stopEmailList(filter: $filter, params: $params) {
      code
      data {
        id
        email
        status
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STOP_EMAIL_GET_QUERY = gql`
  query StopEmailGet($id: ID) {
    stopEmailGet(id: $id) {
      code
      data {
        id
        email
        status
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        userRemoved {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STOP_EMAIL_GET_MANY_QUERY = gql`
  query StopEmailGetMany($ids: [ID]) {
    stopEmailGetMany(ids: $ids) {
      code
      data {
        id
        email
        status
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        userRemoved {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const STOP_EMAIL_CREATE_MUTATION = gql`
  mutation StopEmailCreate($params: StopEmailInput) {
    stopEmailCreate(params: $params) {
      code
      data {
        id
        email
        status
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STOP_EMAIL_UPDATE_MUTATION = gql`
  mutation StopEmailUpdate(
    $where: StopEmailWhereInput
    $params: StopEmailInput
  ) {
    stopEmailUpdate(where: $where, params: $params) {
      code
      data {
        id
        email
        status
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STOP_EMAIL_REMOVE_MUTATION = gql`
  mutation StopEmailRemove($where: StopEmailWhereInput) {
    stopEmailRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STOP_EMAIL_REMOVE_MANY_MUTATION = gql`
  mutation StopEmailRemoveMany($where: [StopEmailWhereInput]) {
    stopEmailRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface StopEmail {
  id?: string | undefined;
  email?: string | undefined;
  status?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
}

export interface StopEmailInput {
  id?: string | null | undefined;
  email?: string | null | undefined;
  status?: number | null | undefined;
}

export interface StopEmailFilter {
  id?: string[] | null | undefined;
  email?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface StopEmailWhereInput {
  id?: string | undefined;
}

/**
 * Stop Email service that handles all stop email-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class StopEmailService {
  /**
   * Get a list of stop emails with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: StopEmailFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(STOP_EMAIL_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single stop email by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(STOP_EMAIL_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple stop emails by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(STOP_EMAIL_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new stop email
   */
  static async create(params: StopEmailInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      STOP_EMAIL_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing stop email
   */
  static async update(
    where: StopEmailWhereInput,
    params: StopEmailInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      STOP_EMAIL_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a stop email
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      STOP_EMAIL_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple stop emails
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      STOP_EMAIL_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
